import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle, Eraser, FilePdf } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SignReturn() {
  const token = window.location.pathname.split("/").filter(Boolean).pop();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/contract/${token}/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B1120";
  }, [done]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
  };

  const submit = async () => {
    if (!hasDrawn.current) return toast.error("Σχεδιάστε πρώτα την υπογραφή σας");
    setBusy(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const res = await fetch(`${BACKEND}/api/public/contract/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Σφάλμα");
      }
      setDone(true);
    } catch (e) {
      toast.error(e.message || "Σφάλμα αποστολής");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <div className="bg-[#234E7D] px-6 py-6 text-white">
        <div className="mx-auto max-w-xl text-2xl font-bold tracking-tight">NAVIPARK</div>
      </div>
      <div className="mx-auto max-w-xl px-6 py-8">
        <div className="rounded-lg border border-border bg-white p-8 shadow-sm" data-testid="sign-card">
          {done || info?.signed ? (
            <div className="text-center" data-testid="sign-done">
              <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500" />
              <h1 className="mt-4 text-xl font-bold text-[#234E7D]">Το συμφωνητικό υπογράφηκε!</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {done
                  ? "Ευχαριστούμε. Θα λάβετε αντίγραφο του υπογεγραμμένου συμφωνητικού στο email σας. Μπορείτε να κλείσετε αυτή τη σελίδα."
                  : "Το συμφωνητικό έχει ήδη υπογραφεί και δεν απαιτείται καμία ενέργεια. Δεν θα αποσταλεί νέο συμφωνητικό."}
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-[#234E7D]">Ψηφιακή υπογραφή συμφωνητικού</h1>
              {info && (
                <div className="mt-3 rounded-sm bg-secondary/60 p-3 text-sm">
                  <div><span className="text-muted-foreground">Εγκατάσταση:</span> <b>{info.marina_name}</b></div>
                  <div><span className="text-muted-foreground">Σκάφος:</span> {info.boat_name} · {info.owner}</div>
                  {info.pdf_url && (
                    <a href={info.pdf_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#0055FF]" data-testid="sign-view-pdf">
                      <FilePdf size={16} weight="bold" /> Προβολή συμφωνητικού (PDF)
                    </a>
                  )}
                </div>
              )}
              <p className="mt-4 text-sm text-muted-foreground">Σχεδιάστε την υπογραφή σας στο πλαίσιο:</p>
              <div className="mt-2 rounded-lg border-2 border-dashed border-[#0055FF]/40 bg-white">
                <canvas
                  ref={canvasRef}
                  width={460}
                  height={190}
                  className="h-[190px] w-full touch-none rounded-lg"
                  onPointerDown={start}
                  onPointerMove={move}
                  onPointerUp={end}
                  onPointerLeave={end}
                  data-testid="sign-canvas"
                />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" onClick={clear} data-testid="sign-clear">
                  <Eraser size={16} weight="bold" /> Καθαρισμός
                </Button>
              </div>
              <Button
                className="mt-4 w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={submit}
                disabled={busy}
                data-testid="sign-submit"
              >
                {busy ? "Αποστολή..." : "Υπογραφή & Αποστολή"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
